import React, { useEffect, useMemo, useState } from "react";
import styles from "../../../../styles/scope3/Dashboard/dashboard.module.css";
import BarChart from "../../../../Components/GraphNCharts/BarChart";
import LineChart from "../../../../Components/GraphNCharts/LineChart";
import { get } from "../../../../utils/api";
import { manipulateDataForBarChart } from "../../../../utils/graphFunctions";
import PeriodFilter from "../../../../Components/Common/PeriodFilter";
import { generateMonthArray } from "../../../../utils/formatDate";
import {
  fixedTons,
  formatNumberCommaSeparated,
} from "../../../../utils/common";
import useAuthStore from "../../../../store/ContextUser";

/** Same month sequence as `generateMonthArray` (for aligning API month rows). */
const enumerateMonthsInRange = (from, to) => {
  const slots = [];
  let month = from.month;
  let year = from.year;
  while (year < to.year || (year === to.year && month <= to.month)) {
    slots.push({ year, month });
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return slots;
};

const stringHash = (text = "") => {
  let hash = 0;
  const value = String(text);
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const franchiseMasterFromItem = (item) =>
  item?.franchiseMaster && typeof item.franchiseMaster === "object"
    ? item.franchiseMaster
    : null;

const franchiseIdLabelFromItem = (item) => {
  const m = franchiseMasterFromItem(item);
  const id = String(
    m?.franchiseId ??
      m?.franchiseID ??
      item?.franchiseId ??
      item?.franchiseID ??
      "",
  ).trim();
  return id || "Unknown";
};

const franchiseNameLabelFromItem = (item) => {
  const m = franchiseMasterFromItem(item);
  const name = String(m?.franchiseName ?? item?.franchiseName ?? "").trim();
  if (name) return name;
  return franchiseIdLabelFromItem(item);
};

const FIGMA_LEASE_ASSET_PALETTE = [
  "rgba(0, 81, 183, 1)", // blue
  "rgba(255, 68, 68, 1)", // red
  "rgba(146, 0, 183, 1)", // purple
  "rgba(72, 130, 87, 1)", // green
  "rgba(255, 165, 0, 1)", // orange
  "rgba(155, 155, 155, 1)", // gray
  "rgba(255, 255, 0, 1)", // yellow
  "rgba(0, 255, 255, 1)", // cyan
  "rgba(255, 0, 255, 1)", // magenta
  "rgba(128, 128, 128, 1)", // light gray
  "rgba(128, 0, 0, 1)", // dark red
  "rgba(128, 128, 0, 1)", // dark yellow
  "rgba(0, 128, 0, 1)", // dark green
  "rgba(0, 0, 128, 1)", // dark blue
  "rgba(128, 0, 128, 1)", // dark magenta
  "rgba(128, 128, 128, 1)", // light gray
  "rgba(128, 0, 0, 1)", // dark red
  "rgba(128, 128, 0, 1)", // dark yellow
  "rgba(0, 128, 0, 1)", // dark green
  "rgba(0, 0, 128, 1)", // dark blue
  "rgba(128, 0, 128, 1)", // dark magenta
  "rgba(128, 128, 128, 1)", // light gray
];

const getLeaseAssetDynamicColor = (label, idx, total) => {
  if (idx < FIGMA_LEASE_ASSET_PALETTE.length) {
    return FIGMA_LEASE_ASSET_PALETTE[idx];
  }
  const seed = stringHash(label) + idx * 37;
  const hue = total > 0 ? Math.round((seed * 137.508) % 360) : 220;
  const saturation = 72;
  const lightness = 48;
  return `hsla(${hue}, ${saturation}%, ${lightness}%, 0.95)`;
};

/** API: `{ data: [ { typeOfAsset, monthWiseEmission: [{ year, month, totalEmission }] } ] }` */
const buildLeaseAssetEmissionDatasets = (rows, fromDate, toDate) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const slots = enumerateMonthsInRange(fromDate, toDate);
  return rows.map((row, idx) => {
    const byKey = new Map();
    (row.monthWiseEmission || []).forEach((m) => {
      byKey.set(`${m.year}-${m.month}`, Number(m.totalEmission) || 0);
    });
    const data = slots.map(({ year, month }) =>
      fixedTons(byKey.get(`${year}-${month}`) ?? 0),
    );
    const label = row.typeOfAsset || `Category ${idx + 1}`;
    const seriesTotal = data.reduce(
      (sum, value) => sum + (Number(value) || 0),
      0,
    );
    const bg = getLeaseAssetDynamicColor(label, idx, seriesTotal);
    return {
      label,
      data,
      backgroundColor: bg,
      borderColor: bg,
      borderWidth: 0.5,
    };
  });
};

const normalizeLeaseAssetRows = (rows) => {
  if (!Array.isArray(rows)) return [];

  return rows
    .map((row, idx) => {
      const typeOfAsset = String(
        row?.typeOfAsset || row?._id || `Category ${idx + 1}`,
      ).trim();

      const monthWiseEmissionSource = Array.isArray(row?.monthWiseEmission)
        ? row.monthWiseEmission
        : Array.isArray(row?.data)
          ? row.data
          : [];

      const monthWiseEmission = monthWiseEmissionSource.map((m) => ({
        year: Number(m?.year) || 0,
        month: Number(m?.month) || 0,
        totalEmission: Number(m?.totalEmission) || 0,
      }));

      const computedTotalEmission = monthWiseEmission.reduce(
        (sum, m) => sum + (Number(m?.totalEmission) || 0),
        0,
      );

      return {
        typeOfAsset,
        totalEmission:
          row?.totalEmission == null
            ? computedTotalEmission
            : Number(row.totalEmission) || 0,
        count: Number(row?.count) || 0,
        monthWiseEmission,
      };
    })
    .filter((row) => row.typeOfAsset);
};

const extractLeaseAssetResponse = (response) => {
  const payload = response?.data;

  if (Array.isArray(payload)) {
    return {
      rows: normalizeLeaseAssetRows(payload),
      isGreaterThan5: false,
    };
  }

  return {
    rows: normalizeLeaseAssetRows(payload?.data),
    isGreaterThan5: Boolean(payload?.isGreaterThan5),
  };
};

const parseMonthToken = (value) => {
  if (!value) return null;
  if (typeof value === "object" && value?.year && value?.month) {
    const year = Number(value.year);
    const month = Number(value.month);
    if (year && month >= 1 && month <= 12) return { year, month };
    return null;
  }
  if (typeof value === "string") {
    const normalized = value.trim();
    const ymMatch = normalized.match(/^(\d{4})-(\d{1,2})$/);
    if (ymMatch) {
      const year = Number(ymMatch[1]);
      const month = Number(ymMatch[2]);
      if (year && month >= 1 && month <= 12) return { year, month };
    }
    const parsed = new Date(normalized);
    if (!Number.isNaN(parsed.getTime())) {
      return { year: parsed.getFullYear(), month: parsed.getMonth() + 1 };
    }
  }
  return null;
};

const buildUtcRangeBounds = (fromDate, toDate) => {
  const startYear = String(fromDate.year);
  const startMonth = String(fromDate.month).padStart(2, "0");
  const endYear = String(toDate.year);
  const endMonth = String(toDate.month).padStart(2, "0");
  const endDay = String(
    new Date(toDate.year, toDate.month, 0).getDate(),
  ).padStart(2, "0");

  return {
    start: `${startYear}-${startMonth}-01T00:00:00.000Z`,
    end: `${endYear}-${endMonth}-${endDay}T23:59:59.999Z`,
  };
};

const Dashboard = () => {
  const currentDate = new Date();
  const { selectedCompany } = useAuthStore();
  const currentYear = currentDate.getFullYear();
  const [selectedFacility, setSelectedFacility] = useState([]);

  const [fromDate, setFromDate] = useState({
    month: 1,
    year: currentYear,
  });
  const [toDate, setToDate] = useState({
    month: currentDate.getMonth() + 1,
    year: currentYear,
  });

  const [dropdownValue, setDropdownValue] = useState({
    businessTravelMode: "Road",
    transportationMode: "Road",
  });

  const [activityEmission, setActivityEmission] = useState(null);
  const [error, setError] = useState(null);

  const [dashboardData, setDashboardData] = useState({
    purchaseGoods: null,
    transportEmissionType: null,
    wasteGenerated: null,
    travelEmission: null,
    transportEmission: null,
    transportTypesEmission: null,
    employeeCommuteEmission: null,
    upstreamLeaseAssetEmission: [],
    downstreamLeaseAssetEmission: [],
    upstreamLeaseAssetIsGreaterThan5: false,
    downstreamLeaseAssetIsGreaterThan5: false,
    franchiseRecords: null,
    investmentRecords: null,
    eolRecords: [],
    useOfSoldProductsOverview: {
      top5ProductsEmission: [],
      top5ProductsCircularityScore: [],
    },
  });

  const activityArr = [
    "Production Materials",
    "Finished Goods",
    "Transportation and Distribution",
    "Information Technology and Communication",
    "Professional Services",
    "Others",
  ];

  const generateYearList = (startYear = 2012) => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = startYear; year <= currentYear; year++) {
      years.push(year);
    }
    return years;
  };
  const yearList = generateYearList(2013);

  const apiurls = {
    emissionActivity: `/dashboard/scope3/purchasegoods/emissionByTypeOfAcitvity?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id}`,
    wasteGenerated: `/dashboard/scope3/wastegenerated/emission?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id}`,
    travelEmission: `/dashboard/scope3/business/emission?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id}`,
    transportEmissionType: `/dashboard/scope3/transportation/overview/emission/transportation-type?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id}`,
    transportEmission: `/dashboard/scope3/transportation/overview/emission?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id}`,
    transportTypesEmission: `/dashboard/scope3/transportation/emission?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id}`,
    employeeCommuteEmission: `/dashboard/scope3/employeecommute/emissionModeofTransportion?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id}`,
    capitalGoodsEmission: `/dashboard/scope3/capitalgoods/capitalGoodsEmission?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id}`,
    upstreamLeaseAssetEmission: `/dashboard/scope3/streamleased/upstreamLeaseAssetEmission?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id || ""}`,
    downstreamLeaseAssetEmission: `/dashboard/scope3/streamleased/downstreamLeaseAssetEmission?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id || ""}`,
    useOfSoldProductsOverview: `/dashboard/scope3/useofsoldproducts/overviewDashboard?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id || ""}`,
    // transportEmissionType: `/dashboard/scope3/transportation/emissionByTypeOfAcitvity`,
  };

  const buildFranchisesOverviewUrl = () => {
    const { start, end } = buildUtcRangeBounds(fromDate, toDate);
    const params = new URLSearchParams({
      page: "1",
      limit: "5000",
      sortField: "createdAt",
      sortOrder: "desc",
      startDate: start,
      endDate: end,
    });
    const facilities = selectedFacility.join(",");
    if (facilities) params.set("facilities", facilities);
    if (selectedCompany?._id) params.set("companyId", selectedCompany._id);
    return `/scope3/franchises/get?${params.toString()}`;
  };

  const buildInvestmentsOverviewUrl = () => {
    const { start, end } = buildUtcRangeBounds(fromDate, toDate);
    const params = new URLSearchParams({
      page: "1",
      limit: "5000",
      sortField: "createdAt",
      sortOrder: "desc",
      startDate: start,
      endDate: end,
    });
    const facilities = selectedFacility.join(",");
    if (facilities) params.set("facilities", facilities);
    if (selectedCompany?._id) params.set("companyId", selectedCompany._id);
    return `/scope3/investments/get?${params.toString()}`;
  };

  const buildEolOverviewUrl = () => {
    const { start, end } = buildUtcRangeBounds(fromDate, toDate);
    const params = new URLSearchParams({
      page: "1",
      limit: "5000",
      sortField: "createdAt",
      sortOrder: "desc",
      startDate: start,
      endDate: end,
    });
    const facilities = selectedFacility.join(",");
    if (facilities) params.set("facilityList", facilities);
    if (selectedCompany?._id) params.set("companyId", selectedCompany._id);
    return `/scope3/eolTreatmentOfSoldProducts/get?${params.toString()}`;
  };

  /** EOL list API nests product fields under populated productMaster. */
  const mapEolOverviewRecord = (item) => {
    if (!item || typeof item !== "object") return item;
    const pm =
      item.productMaster && typeof item.productMaster === "object"
        ? item.productMaster
        : null;
    const w = pm?.averageWeightPerUnit ?? pm?.weight;
    return {
      ...item,
      productName:
        String(pm?.productName ?? item.productName ?? "").trim() ||
        item.productName,
      averageWeightPerUnit:
        w !== undefined && w !== null && w !== ""
          ? w
          : item.averageWeightPerUnit,
    };
  };

  const fetchData = async () => {
    try {
      const [
        emissionActivity,
        wasteGenerated,
        travelEmission,
        transportEmissionType,
        transportEmission,
        transportTypesEmission,
        employeeCommuteEmission,
        capitalGoodsEmission,
        upstreamLeaseAssetEmission,
        downstreamLeaseAssetEmission,
        franchiseListRes,
        investmentListRes,
        eolListRes,
        useOfSoldProductsOverviewRes,
      ] = await Promise.all([
        get(apiurls.emissionActivity),
        get(apiurls.wasteGenerated),
        get(apiurls.travelEmission),
        get(apiurls.transportEmissionType),
        get(apiurls.transportEmission),
        get(apiurls.transportTypesEmission),
        get(apiurls.employeeCommuteEmission),
        get(apiurls.capitalGoodsEmission),
        get(apiurls.upstreamLeaseAssetEmission),
        get(apiurls.downstreamLeaseAssetEmission),
        get(buildFranchisesOverviewUrl()).catch(() => ({ data: { data: [] } })),
        get(buildInvestmentsOverviewUrl()).catch(() => ({
          data: { data: [] },
        })),
        get(buildEolOverviewUrl()).catch(() => ({ data: { data: [] } })),
        get(apiurls.useOfSoldProductsOverview).catch(() => ({
          data: {
            top5ProductsEmission: [],
            top5ProductsCircularityScore: [],
          },
        })),
      ]);

      const upstreamLeaseAssetParsed = extractLeaseAssetResponse(
        upstreamLeaseAssetEmission,
      );
      const downstreamLeaseAssetParsed = extractLeaseAssetResponse(
        downstreamLeaseAssetEmission,
      );

      setDashboardData({
        emissionActivity: emissionActivity.data,
        wasteGenerated: wasteGenerated.data,
        travelEmission: travelEmission.data,
        transportEmissionType: transportEmissionType.data,
        transportEmission: transportEmission.data,
        transportTypesEmission: transportTypesEmission.data,
        employeeCommuteEmission: employeeCommuteEmission.data,
        capitalGoodsEmission: capitalGoodsEmission.data,
        upstreamLeaseAssetEmission: upstreamLeaseAssetParsed.rows,
        upstreamLeaseAssetIsGreaterThan5:
          upstreamLeaseAssetParsed.isGreaterThan5,
        downstreamLeaseAssetEmission: downstreamLeaseAssetParsed.rows,
        downstreamLeaseAssetIsGreaterThan5:
          downstreamLeaseAssetParsed.isGreaterThan5,
        franchiseRecords: franchiseListRes?.data?.data || [],
        investmentRecords: investmentListRes?.data?.data || [],
        eolRecords: (eolListRes?.data?.data || []).map(mapEolOverviewRecord),
        useOfSoldProductsOverview: {
          top5ProductsEmission:
            useOfSoldProductsOverviewRes?.data?.top5ProductsEmission || [],
          top5ProductsCircularityScore:
            useOfSoldProductsOverviewRes?.data?.top5ProductsCircularityScore ||
            useOfSoldProductsOverviewRes?.data?.circularityScoreData ||
            [],
        },
      });
    } catch (error) {
      setError("Error fetching data: " + error.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, selectedFacility, selectedCompany]);

  useEffect(() => {
    let data = dashboardData?.emissionActivity?.graphData;
    let dataMap = new Map();
    let others = Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      totalEmission: 0,
    }));

    data &&
      data?.forEach((item) => {
        if (activityArr.includes(item._id)) {
          dataMap.set(item._id, item);
        } else {
          // item?.data.forEach((month, i) => {
          //   others[i].totalEmission += month?.totalEmission ?? 0;
          // });
        }
      });

    let completeData = activityArr.map(
      (category) =>
        dataMap.get(category) || {
          _id: category,
          data: others.map((m) => ({ ...m, totalEmission: 0 })),
        },
    );

    // if (others.some((month) => month.totalEmission > 0)) {
    //   completeData.push({ _id: "Others", data: others });
    // }

    setActivityEmission(completeData);
  }, [dashboardData, selectedCompany]);

  console.log("first", dashboardData);

  const emissionActivityColor = [
    { label: "Production Materials", color: "rgba(0, 102, 231, 1)" },
    { label: "Finished Goods", color: "rgba(255, 99, 132, 1)" },
    {
      label: "Transportation and Distribution",
      color: "rgba(75, 192, 192, 1)",
    },
    {
      label: "Information Technology and Communication",
      color: "rgba(255, 159, 64, 1)",
    },
    { label: "Professional Services", color: "rgba(153, 102, 255, 1)" },
    { label: "Others", color: "rgba(54, 162, 235, 1)" },
  ];

  let emissionColors = [
    { label: "Hazardous Waste", color: "rgba(255, 79, 79, 1)" },
    { label: "Non-Hazardous Waste", color: "rgba(146, 0, 183, 1)" },
    { label: "Construction & Demolition Waste", color: "rgba(14, 98, 224, 1)" },
    { label: "Wastewater", color: "rgba(155, 155, 155, 1)" },
  ];

  let businessTravelColor = [
    { label: "Road", color: "rgba(255, 79, 79, 1)" },
    { label: "Rail", color: "rgba(146, 0, 183, 1)" },
    { label: "Air", color: "rgba(14, 98, 224, 1)" },
    { label: "Ship", color: "rgba(60, 215, 21, 1)" },
    { label: "Water", color: "rgba(60, 215, 21, 1)" },
  ];

  let emissionActivityDataset = manipulateDataForBarChart({
    data: dashboardData?.emissionActivity?.graphData,
    colors: emissionActivityColor,
    type: "purchaseEmissionData",
  });

  let emissionDataSet = manipulateDataForBarChart({
    data: dashboardData?.wasteGenerated?.emissionData,
    colors: emissionColors,
  });

  const upstreamLeaseAssetDatasets = buildLeaseAssetEmissionDatasets(
    dashboardData?.upstreamLeaseAssetEmission,
    fromDate,
    toDate,
  );
  const downstreamLeaseAssetDatasets = buildLeaseAssetEmissionDatasets(
    dashboardData?.downstreamLeaseAssetEmission,
    fromDate,
    toDate,
  );

  const upstreamLeaseAssetTotal = (
    dashboardData?.upstreamLeaseAssetEmission || []
  ).reduce((sum, row) => sum + (Number(row?.totalEmission) || 0), 0);
  const downstreamLeaseAssetTotal = (
    dashboardData?.downstreamLeaseAssetEmission || []
  ).reduce((sum, row) => sum + (Number(row?.totalEmission) || 0), 0);
  const franchiseEmissionByFranchiseChart = useMemo(() => {
    const list = dashboardData?.franchiseRecords;
    if (!Array.isArray(list) || list.length === 0) {
      return { labels: [], datasets: [], totalEmission: 0 };
    }
    const map = new Map();
    list.forEach((item) => {
      const labelKey = franchiseNameLabelFromItem(item);
      const s1 = Number(item?.scope1EmissionsMetricTon) || 0;
      const s2 = Number(item?.scope2EmissionsMetricTon) || 0;
      const s3 = Number(item?.scope3EmissionsMetricTon) || 0;
      const cur = map.get(labelKey) || { s1: 0, s2: 0, s3: 0 };
      map.set(labelKey, { s1: cur.s1 + s1, s2: cur.s2 + s2, s3: cur.s3 + s3 });
    });
    const rows = Array.from(map.entries())
      .map(([franchiseLabel, v]) => ({ franchiseLabel, ...v }))
      .sort((a, b) => b.s1 + b.s2 + b.s3 - (a.s1 + a.s2 + a.s3));
    const labels = rows.map((r) => r.franchiseLabel);
    const toPoint = (v) => fixedTons(v);
    const datasets = [
      {
        label: "Scope 1",
        data: rows.map((r) => toPoint(r.s1)),
        backgroundColor: "#0B5ED7",
        borderColor: "#0B5ED7",
        borderWidth: 0.5,
      },
      {
        label: "Scope 2",
        data: rows.map((r) => toPoint(r.s2)),
        backgroundColor: "#FC4A4A",
        borderColor: "#FC4A4A",
        borderWidth: 0.5,
      },
      {
        label: "Scope 3",
        data: rows.map((r) => toPoint(r.s3)),
        backgroundColor: "#F8B400",
        borderColor: "#F8B400",
        borderWidth: 0.5,
      },
    ];
    const totalEmission = rows.reduce((acc, r) => acc + r.s1 + r.s2 + r.s3, 0);
    return { labels, datasets, totalEmission };
  }, [dashboardData?.franchiseRecords]);

  const investmentEmissionByTypeChart = useMemo(() => {
    const list = dashboardData?.investmentRecords;
    if (!Array.isArray(list) || list.length === 0) {
      return { labels: [], datasets: [], totalEmission: 0 };
    }
    const map = new Map();
    list.forEach((item) => {
      const type = String(item?.investmentType ?? "").trim() || "Unknown";
      const s1 = Number(item?.scope1EmissionsMetricTon) || 0;
      const s2 = Number(item?.scope2EmissionsMetricTon) || 0;
      const s3 = Number(item?.scope3EmissionsMetricTon) || 0;
      const cur = map.get(type) || { s1: 0, s2: 0, s3: 0 };
      map.set(type, { s1: cur.s1 + s1, s2: cur.s2 + s2, s3: cur.s3 + s3 });
    });
    const rows = Array.from(map.entries())
      .map(([investmentType, v]) => ({ investmentType, ...v }))
      .sort((a, b) => b.s1 + b.s2 + b.s3 - (a.s1 + a.s2 + a.s3));
    const labels = rows.map((r) => r.investmentType);
    const toPoint = (v) => fixedTons(v);
    const datasets = [
      {
        label: "Scope 1",
        data: rows.map((r) => toPoint(r.s1)),
        backgroundColor: "#0B5ED7",
        borderColor: "#0B5ED7",
        borderWidth: 0.5,
      },
      {
        label: "Scope 2",
        data: rows.map((r) => toPoint(r.s2)),
        backgroundColor: "#FC4A4A",
        borderColor: "#FC4A4A",
        borderWidth: 0.5,
      },
      {
        label: "Scope 3",
        data: rows.map((r) => toPoint(r.s3)),
        backgroundColor: "#F8B400",
        borderColor: "#F8B400",
        borderWidth: 0.5,
      },
    ];
    const totalEmission = rows.reduce((acc, r) => acc + r.s1 + r.s2 + r.s3, 0);
    return { labels, datasets, totalEmission };
  }, [dashboardData?.investmentRecords]);

  const useOfSoldProductsTopEmissionChart = useMemo(() => {
    const list = dashboardData?.useOfSoldProductsOverview?.top5ProductsEmission;
    if (!Array.isArray(list) || list.length === 0) {
      return { labels: [], datasets: [], totalEmission: 0 };
    }

    const rows = list
      .map((item, idx) => ({
        productName: String(item?._id || `Product ${idx + 1}`).trim(),
        totalEmission: Number(item?.totalEmission) || 0,
      }))
      .filter((item) => item.productName)
      .sort((a, b) => b.totalEmission - a.totalEmission)
      .slice(0, 5);

    return {
      labels: rows.map((item) => item.productName),
      datasets: [
        {
          label: "",
          data: rows.map((item) => item.totalEmission),
          backgroundColor: "#EF8E10",
          borderColor: "#EF8E10",
          borderWidth: 0,
        },
      ],
      totalEmission: rows.reduce(
        (sum, item) => sum + (Number(item.totalEmission) || 0),
        0,
      ),
    };
  }, [dashboardData?.useOfSoldProductsOverview?.top5ProductsEmission]);

  const useOfSoldProductsCircularityDataset = useMemo(() => {
    const list =
      dashboardData?.useOfSoldProductsOverview?.top5ProductsCircularityScore ||
      dashboardData?.useOfSoldProductsOverview?.circularityScoreData;
    if (!Array.isArray(list) || list.length === 0) return [];

    const slots = enumerateMonthsInRange(fromDate, toDate);
    return list.map((item, idx) => {
      const byKey = new Map();
      (item?.data || []).forEach((point) => {
        byKey.set(
          `${Number(point?.year) || 0}-${Number(point?.month) || 0}`,
          Number(point?.circularityScore) || 0,
        );
      });
      const color = getLeaseAssetDynamicColor(
        item?._id || `Product ${idx + 1}`,
        idx,
        list.length,
      );

      return {
        label: item?._id || `Product ${idx + 1}`,
        data: slots.map(({ year, month }) =>
          Number((byKey.get(`${year}-${month}`) || 0).toFixed(2)),
        ),
        borderColor: color,
        backgroundColor: color,
        tension: 0.4,
        fill: false,
        pointRadius: 2,
      };
    });
  }, [
    dashboardData?.useOfSoldProductsOverview?.top5ProductsCircularityScore,
    dashboardData?.useOfSoldProductsOverview?.circularityScoreData,
    fromDate,
    toDate,
  ]);

  let businessTravelDataset = manipulateDataForBarChart({
    data: dashboardData?.travelEmission?.emissionData,
    colors: businessTravelColor,
    type: "businessTravelEmission",
  });

  let employeeCommuteDataset = manipulateDataForBarChart({
    data: dashboardData?.employeeCommuteEmission?.data,
    colors: businessTravelColor,
    type: "employeeCommuteEmission",
  });

  const capitalGoodsRows = (
    Array.isArray(dashboardData?.capitalGoodsEmission?.data)
      ? dashboardData.capitalGoodsEmission.data
      : []
  ).map((item) => ({
    typeOfAsset: item?._id || "Other",
    monthWiseEmission: (item?.data || []).map((monthItem) => ({
      year: Number(monthItem?.year) || fromDate.year,
      month: Number(monthItem?.month) || 0,
      totalEmission: Number(monthItem?.totalEmission) || 0,
    })),
  }));

  const capitalGoodsDataset = buildLeaseAssetEmissionDatasets(
    capitalGoodsRows,
    fromDate,
    toDate,
  );

  const capitalGoodsTotal = (
    dashboardData?.capitalGoodsEmission?.data || []
  ).reduce(
    (sum, category) =>
      sum +
      (category?.data || []).reduce(
        (innerSum, month) => innerSum + (Number(month?.totalEmission) || 0),
        0,
      ),
    0,
  );

  let transportationDataset =
    dashboardData?.transportTypesEmission?.data
      ?.map((el) =>
        el?.twelveMonthData?.map((e) => e?.totalEmission)?.filter(Boolean),
      )
      ?.filter((e) => e?.length > 0)?.length > 0
      ? manipulateDataForBarChart({
          data: dashboardData?.transportTypesEmission?.data,
          colors: businessTravelColor,
          type: "transportationEmission",
        })
      : [];

  const handleDropdownValueChange = (e, type) => {
    if (type === "transportationMode") {
      setDropdownValue((prev) => ({
        ...prev,
        transportationMode: e.target.value,
      }));
    } else if (type === "businessTravelMode") {
      setDropdownValue((prev) => ({
        ...prev,
        businessTravelMode: e.target.value,
      }));
    }
  };

  let transportationModeArr = ["Road", "Rail", "Air", "Ship"];

  const carbonColors = ["#0066E7", "#FF4444"];
  const carbonLabels = ["Upstream", "Downstream"];

  const transportTypes = ["air", "road", "rail", "ship"];

  const eolMonthlyLabels = useMemo(
    () => generateMonthArray(fromDate, toDate),
    [fromDate, toDate],
  );

  const eolTopEmissionProductsDataset = useMemo(() => {
    const records = Array.isArray(dashboardData?.eolRecords)
      ? dashboardData.eolRecords
      : [];
    if (records.length === 0) return [];

    const scopeMonths = enumerateMonthsInRange(fromDate, toDate);
    const slotByKey = new Map(
      scopeMonths.map((slot, idx) => [`${slot.year}-${slot.month}`, idx]),
    );
    const productMap = new Map();

    records.forEach((item) => {
      const productName = String(item?.productName || "").trim() || "Unknown";
      const totalEmission = Number(item?.totalEmission) || 0;
      if (totalEmission <= 0) return;

      const productionPoint = parseMonthToken(
        item?.eolMonth || item?.createdAt,
      );
      if (!productionPoint) return;
      const activeMonthKey = `${productionPoint.year}-${productionPoint.month}`;
      if (!slotByKey.has(activeMonthKey)) return;

      const current =
        productMap.get(productName) ||
        Array.from({ length: scopeMonths.length }, () => 0);
      const idx = slotByKey.get(activeMonthKey);
      if (typeof idx === "number") current[idx] += totalEmission;
      productMap.set(productName, current);
    });

    const rows = Array.from(productMap.entries())
      .map(([name, data]) => ({
        name,
        data: data.map((v) => fixedTons(v)),
        total: data.reduce((sum, val) => sum + val, 0),
      }))
      .filter((row) => row.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);

    return rows.map((row, idx) => {
      const bg = getLeaseAssetDynamicColor(row.name, idx, row.total);
      return {
        label: row.name,
        data: row.data,
        backgroundColor: bg,
        borderColor: bg,
        borderWidth: 0.5,
      };
    });
  }, [dashboardData?.eolRecords, fromDate, toDate]);

  const eolIntensityByProduct = useMemo(() => {
    const records = Array.isArray(dashboardData?.eolRecords)
      ? dashboardData.eolRecords
      : [];
    if (records.length === 0) return { labels: [], values: [] };

    const map = new Map();
    records.forEach((item) => {
      const productName = String(item?.productName || "").trim() || "Unknown";
      const emission = Number(item?.totalEmission) || 0;
      const units = Number(item?.numberOfUnitsSold) || 0;
      const avgWeightPerUnitTon = Number(item?.averageWeightPerUnit) || 0;
      const curr = map.get(productName) || {
        emission: 0,
        avgWeightSum: 0,
        unitsSum: 0,
      };
      map.set(productName, {
        emission: curr.emission + emission,
        avgWeightSum: curr.avgWeightSum + avgWeightPerUnitTon,
        unitsSum: curr.unitsSum + units,
      });
    });

    const rows = Array.from(map.entries())
      .map(([name, val]) => {
        const safeUnitsSum = val.unitsSum === 0 ? 1 : val.unitsSum;
        return {
          name,
          intensity:
            val.avgWeightSum > 0
              ? val.emission / (val.avgWeightSum * safeUnitsSum)
              : 0,
        };
      })
      .filter((row) => row.intensity > 0)
      .sort((a, b) => b.intensity - a.intensity)
      .slice(0, 5);

    return {
      labels: rows.map((row) => row.name),
      values: rows.map((row) => Number((row.intensity || 0).toFixed(4))),
    };
  }, [dashboardData?.eolRecords]);

  const eolIntensityDataset = useMemo(
    () => [
      {
        label: "Emission Intensity",
        data: eolIntensityByProduct.values,
        backgroundColor: "rgba(46, 119, 228, 0.95)",
        borderColor: "rgba(46, 119, 228, 1)",
        borderWidth: 0.5,
      },
    ],
    [eolIntensityByProduct.values],
  );
  const formatEolIntensityTick = (value) => Number(value || 0).toFixed(4);

  const createDataset = (transportType) => {
    const transportData =
      dashboardData?.transportEmissionType?.[`${transportType}Data`] || [];

    return carbonLabels
      ?.map((label, i) => {
        const categoryData = transportData.find((entry) => entry._id === label);

        const emissions =
          categoryData?.data?.map((item) => item?.emission) || [];

        // If no valid emissions, skip this dataset
        if (!emissions.some(Boolean)) return null;

        return {
          label,
          data: emissions,
          borderColor: carbonColors[i],
          backgroundColor: carbonColors[i],
          tension: 0.4,
          fill: true,
          pointBorderColor: "#fff",
          pointRadius: 0,
        };
      })
      .filter(Boolean); // remove null entries
  };

  const allTransportDatasets = transportTypes.reduce((acc, type) => {
    acc[type] = createDataset(type)?.filter(Boolean);
    return acc;
  }, {});

  console.log("first56", allTransportDatasets);

  return (
    <>
      <div className={styles.heading_container}>
        <p className={styles.heading}>Scope 3</p>
        <PeriodFilter
          {...{
            fromDate,
            setFromDate,
            toDate,
            setToDate,
            yearList,
            setSelectedFacility,
            selectedFacility,
          }}
        />
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>Purchase Goods & Services</p>
        <div className={styles.graph_container}>
          <p className={styles.card_heading}>
            Total Emissions by Type of Activities (tCO2e)
          </p>
          <p className={styles.sub_card_heading}>
            {formatNumberCommaSeparated(
              fixedTons(dashboardData?.emissionActivity?.totalEmission ?? 0),
            )}
          </p>
          <BarChart
            datasets={emissionActivityDataset}
            labels={generateMonthArray(fromDate, toDate)}
            display={true}
          />
        </div>
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>Capital Goods</p>
        <div className={styles.graph_container}>
          <p className={styles.card_heading}>
            Emission by Capital Goods Category (tCO2e)
          </p>
          <BarChart
            datasets={capitalGoodsDataset}
            labels={generateMonthArray(fromDate, toDate)}
            display={true}
            yAxisTitle="Emission (tCO2e)"
          />
        </div>
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>Transportation and Distribution</p>
        <div className={styles.graph_container}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <p className={styles.card_heading}>Total Emissions (tCO2e)</p>
              <p className={styles.sub_card_heading}>
                {dropdownValue?.transportationMode === "Rail"
                  ? formatNumberCommaSeparated(
                      fixedTons(
                        dashboardData?.transportTypesEmission?.data?.find(
                          (item) => item.emission === "Rail",
                        )?.annualSum || 0,
                      ),
                    )
                  : dropdownValue?.transportationMode === "Air"
                    ? formatNumberCommaSeparated(
                        fixedTons(
                          dashboardData?.transportTypesEmission?.data?.find(
                            (item) => item.emission === "Air",
                          )?.annualSum || 0,
                        ),
                      )
                    : dropdownValue?.transportationMode === "Ship"
                      ? formatNumberCommaSeparated(
                          fixedTons(
                            dashboardData?.transportTypesEmission?.data?.find(
                              (item) => item.emission === "Water",
                            )?.annualSum || 0,
                          ),
                        )
                      : dropdownValue?.transportationMode === "Road"
                        ? formatNumberCommaSeparated(
                            fixedTons(
                              dashboardData?.transportTypesEmission?.data?.find(
                                (item) => item.emission === "Road",
                              )?.annualSum || 0,
                            ),
                          )
                        : 0}
              </p>
            </div>
            <div>
              <select
                style={{ width: "200px" }}
                name="month"
                id="month"
                value={dropdownValue.transportationMode}
                onChange={(e) =>
                  handleDropdownValueChange(e, "transportationMode")
                }
                className={styles.heading_select_month}
              >
                {transportationModeArr.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <BarChart
            datasets={transportationDataset?.filter(Boolean)}
            labels={
              transportationDataset?.length > 0
                ? generateMonthArray(fromDate, toDate)
                : []
            }
            display={true}
          />
        </div>
        <div className={styles.transport_container}>
          <div className={styles.transport_container_div}>
            <p className={styles.card_heading}>
              Road Transport Emission (tCO2e)
            </p>
            <p className={styles.sub_card_heading}>
              {formatNumberCommaSeparated(
                fixedTons(
                  dashboardData?.transportEmissionType?.roadTotalEmission ?? 0,
                ),
              )}
            </p>
            <LineChart
              dataset={allTransportDatasets?.road}
              labels={generateMonthArray(fromDate, toDate)}
              display={true}
              yAxisTitle={"Emissions (tCO2e)"}
              // customWidth={410}
              // customHeight={250}
            />
          </div>
          <div className={styles.transport_container_div}>
            <p className={styles.card_heading}>
              Air Transport Emission (tCO2e)
            </p>
            <p className={styles.sub_card_heading}>
              {formatNumberCommaSeparated(
                fixedTons(
                  dashboardData?.transportEmissionType?.airTotalEmission ?? 0,
                ),
              )}
            </p>
            <LineChart
              dataset={allTransportDatasets?.air}
              labels={generateMonthArray(fromDate, toDate)}
              display={true}
              yAxisTitle={"Emissions (tCO2e)"}
              // customWidth={410}
              // customHeight={250}
            />
          </div>
          <div className={styles.transport_container_div}>
            <p className={styles.card_heading}>
              Rail Transport Emission (tCO2e)
            </p>
            <p className={styles.sub_card_heading}>
              {formatNumberCommaSeparated(
                fixedTons(
                  dashboardData?.transportEmissionType?.railTotalEmission ?? 0,
                ),
              )}
            </p>
            <LineChart
              dataset={allTransportDatasets?.rail}
              labels={generateMonthArray(fromDate, toDate)}
              display={true}
              yAxisTitle={"Emissions (tCO2e)"}
              // customWidth={410}
              // customHeight={250}
            />
          </div>
          <div className={styles.transport_container_div}>
            <p className={styles.card_heading}>
              Water Transport Emission (tCO2e)
            </p>
            <p className={styles.sub_card_heading}>
              {formatNumberCommaSeparated(
                fixedTons(
                  dashboardData?.transportEmissionType?.shipTotalEmission ?? 0,
                ),
              )}
            </p>
            <LineChart
              dataset={allTransportDatasets?.ship}
              labels={generateMonthArray(fromDate, toDate)}
              display={true}
              yAxisTitle={"Emissions (tCO2e)"}
            />
          </div>
        </div>
      </div>

      <div className={styles.waste_container}>
        <p className={styles.sub_heading}>Waste Generated in Operations</p>
        <div className={styles.graph_container}>
          <p className={styles.card_heading}>
            Emissions from Waste Management (tCO2e)
          </p>
          <p className={styles.sub_card_heading}>
            {formatNumberCommaSeparated(
              fixedTons(dashboardData?.wasteGenerated?.totalEmission ?? 0),
            )}
          </p>
          <BarChart
            datasets={emissionDataSet}
            labels={generateMonthArray(fromDate, toDate)}
            display={true}
          />
        </div>
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>Business Travel</p>
        <div className={styles.card_container}>
          <p className={styles.card_heading}>
            Emissions by Mode of Travel (tCO2e)
          </p>
          <p className={styles.sub_card_heading}>
            {formatNumberCommaSeparated(
              fixedTons(dashboardData?.travelEmission?.emissionByFilterDate) ??
                0,
            )}
          </p>
        </div>
        <div className={styles.graph_container}>
          <p className={styles.card_heading}>
            Emissions by Mode of Travel (tCO2e)
          </p>
          <p className={styles.sub_card_heading}>
            {formatNumberCommaSeparated(
              fixedTons(dashboardData?.travelEmission?.totalEmission) ?? 0,
            )}
          </p>
          <BarChart
            datasets={businessTravelDataset}
            labels={generateMonthArray(fromDate, toDate)}
            display={true}
          />
        </div>
        {/* <div className={styles.graph_container}>
          <p className={styles.card_heading}>Hotel Stay Emissions</p>
          <p className={styles.sub_card_heading}>2000 Tons Co2eq</p>
          <BarChart datasets={dataset} labels={monthSort} />
        </div> */}
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>Employee Commute</p>
        <div className={styles.graph_container}>
          <p className={styles.card_heading}>
            Total Emissions by Mode of Transportation (tCO2e)
          </p>
          <p className={styles.sub_card_heading}>
            {isNaN(
              Number(dashboardData?.employeeCommuteEmission?.totalEmission),
            ) || dashboardData?.employeeCommuteEmission?.totalEmission === "NaN"
              ? "0"
              : formatNumberCommaSeparated(
                  fixedTons(
                    Number(dashboardData.employeeCommuteEmission.totalEmission),
                  ),
                )}
          </p>
          <BarChart
            datasets={employeeCommuteDataset}
            labels={generateMonthArray(fromDate, toDate)}
            display={true}
          />
        </div>
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>Upstream Leased Assets</p>
        <div className={styles.graph_container}>
          <p className={styles.card_heading}>
            Emission hotspot (most emitting asset)
          </p>
          {dashboardData?.upstreamLeaseAssetIsGreaterThan5 && (
            <p className={styles.graph_sub_heading}>
              *Note: Top 5 emission-contributing assets
            </p>
          )}
          <p className={styles.sub_card_heading}>
            {formatNumberCommaSeparated(fixedTons(upstreamLeaseAssetTotal))}
          </p>
          <BarChart
            datasets={upstreamLeaseAssetDatasets}
            labels={generateMonthArray(fromDate, toDate)}
            display={true}
            yAxisTitle="Emission (tCO2e)"
          />
        </div>
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>
          End of Life Treatment of Sold Products
        </p>
        <div className={styles.graph_container}>
          <p className={styles.card_heading}>
            Top 5 Emission Intensive Products as per End of Life Treatment
            (tCO2e)
          </p>
          <BarChart
            datasets={eolTopEmissionProductsDataset}
            labels={eolMonthlyLabels}
            display={true}
            yAxisTitle="Emission (tCO2e)"
          />
        </div>
        <div className={styles.graph_container}>
          <p className={styles.card_heading}>
            Emission Intensity per tonne of product sold (tCO2e/MT of product
            sold)
          </p>
          <BarChart
            datasets={eolIntensityDataset}
            labels={eolIntensityByProduct.labels}
            display={false}
            yAxisTitle="Emission (tCO2e)"
            linearTickFormatter={formatEolIntensityTick}
          />
        </div>
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>Downstream Leased Assets</p>
        <div className={styles.graph_container}>
          <p className={styles.card_heading}>
            Emission hotspot (most emitting asset)
          </p>
          {dashboardData?.downstreamLeaseAssetIsGreaterThan5 && (
            <p className={styles.graph_sub_heading}>
              Top 5 emission-contributing assets
            </p>
          )}
          <p className={styles.sub_card_heading}>
            {formatNumberCommaSeparated(fixedTons(downstreamLeaseAssetTotal))}
          </p>
          <BarChart
            datasets={downstreamLeaseAssetDatasets}
            labels={generateMonthArray(fromDate, toDate)}
            display={true}
            yAxisTitle="Emission (tCO2e)"
          />
        </div>
      </div>

      <div className={styles.waste_container}>
        <p className={styles.sub_heading}>Franchise</p>
        <div className={styles.graph_container}>
          <p className={styles.card_heading}>Emission per franchise</p>
          {/* <p className={styles.sub_card_heading}>
          {formatNumberCommaSeparated(fixedTons(franchiseEmissionByFranchiseChart.totalEmission ?? 0))}
          </p> */}
          <BarChart
            datasets={franchiseEmissionByFranchiseChart.datasets}
            labels={franchiseEmissionByFranchiseChart.labels}
            display={true}
            yAxisTitle="Emission (tCO2e)"
            ellipsis={false}
            customHeight="300px"
          />
        </div>
      </div>

      {/* <div className={styles.waste_container}>
        <p className={styles.sub_heading}>Investments</p>
        <div className={styles.graph_container}>
          <p className={styles.card_heading}>Emission per Investment Type</p>
          <BarChart
            datasets={investmentEmissionByTypeChart.datasets}
            labels={investmentEmissionByTypeChart.labels}
            display={true}
            yAxisTitle="Emission (tCO2e)"
            ellipsis={false}
            customHeight="300px"
          />
        </div>
      </div> */}

      {/* <div className={styles.waste_container}>
        <p className={styles.sub_heading}>Use of Sold Products</p>
        <div className={styles.graph_container}>
          <p className={styles.card_heading}>Top 5 Products Emission</p>
          <p className={styles.sub_card_heading}>
            {formatNumberCommaSeparated(
              fixedTons(useOfSoldProductsTopEmissionChart.totalEmission ?? 0),
            )}
          </p>
          <BarChart
            datasets={useOfSoldProductsTopEmissionChart.datasets}
            labels={useOfSoldProductsTopEmissionChart.labels}
            display={false}
            indexAxis="y"
            horizontalCleanStyle
            valueAxisTitle="Emission (tCO2e)"
            ellipsis={false}
            customHeight="300px"
            linearTickFormatter={(value) =>
              formatNumberCommaSeparated(fixedTons(value))
            }
          />
        </div>
      </div> */}
    </>
  );
};

export default Dashboard;
